// The widget registry — the canonical allowlist of widget keys + each key's
// response contract. One source of truth shared by:
//   • the app-api seeder — iterates WIDGET_KEYS, writes one cache item per
//     (fixture × widget);
//   • the generic widget reader — validates the {key} path param against
//     WIDGET_KEYS (404 on anything not here);
//   • the frontend (slice 4) — `useWidgetData<WidgetPayloadMap[K]>(key)`.
//
// The key is the kebab-case slug used in the endpoint path and the DynamoDB
// sk (`WIDGET#{key}`); WIDGET_TITLES ties each key back to its module title.
//
// CONTRACT = the payload the endpoint returns = the widget's FULL dataset (all
// control variants); the client shapes it by the active controls (so toggles
// stay instant — no refetch). For most widgets that's exactly the type the
// `legacyData` getter returns today, so the dummy→real swap in M3 is a no-op.
// A few widgets read MORE than one dataset (the blocks view of a rate donut
// needs recent form; a movement chart needs its series + labels + bookmaker
// offsets; Value Indicator aggregates five markets) — those get a small
// **envelope** type here so the endpoint returns one object.
//
// PURE: no window, no AWS. Which raw dummy dataset feeds each key, and how the
// envelopes are assembled, lives with the seeder in app-api (`seed/builders.ts`)
// — that's where knowledge of the dummy-data shape belongs. This file is only
// the key list + the response types.

import type { FormData } from "./form.js";
import type { InjuryData, DoubtfulData, NewsData } from "./teamNews.js";
import type { PenaltyTakersData } from "./penaltyTakers.js";
import type { PlayerDisciplineData } from "./playerDiscipline.js";
import type {
  WinnerData,
  GoalsCardsData,
  PlayerMarketData,
  ValueIndicatorSources,
} from "./valueIndicator.js";
import type { MarginCalculatorData } from "./marginCalculator.js";
import type { TopScorersLeagueData, TopScorersClubData } from "./topScorers.js";
import type { AssistsLeagueData, AssistsClubData } from "./assists.js";
import type { ShotsOnTargetData } from "./shotsOnTarget.js";
import type { TeamDisciplineData } from "./teamDiscipline.js";
import type { ScoredConcededData, PenaltyRecordData } from "./statPair.js";
import type { OverUnderData } from "./overUnder.js";
import type { FirstGoalData } from "./firstGoal.js";
import type { SetPieceData } from "./setPiece.js";
import type { RateData } from "./matchRate.js";
import type { GtbData } from "./goalsByTimeBand.js";
import type { RefereeProfileData } from "./refereeProfile.js";
import type { RefWithTeamsData } from "./refWithTeams.js";
import type { RefTrendData } from "./refTrend.js";
import type { HalfGoalsData, HalfGoalsBandsData } from "./halfGoals.js";
import type { H2HData } from "./h2h.js";
import type { MatchStatesData } from "./matchStates.js";
import type { TrajectoryData, TrajectoryMatchInfoData } from "./trajectory.js";
import type { XgTimelineData } from "./xgTimeline.js";
import type {
  GoalsCardsMovementData,
  WinnerMovementData,
  PlayerMovementData,
  MovementLabels,
  BkOffsets,
} from "./movement.js";
import type { CorrectScoreGrid } from "./correctScore.js";
import type { MovementHistoryData, LineHistoryData } from "./movementHistory.js";
import type { LeagueTeam } from "./leagueTable.js";
import type { XgTeam } from "./xgTable.js";

// ─── Envelope payloads (widgets that read more than one dataset) ─────────────

/** Home/Away Form — the venue toggle picks one; the endpoint ships both. */
export interface HomeAwayFormPayload {
  home: FormData;
  away: FormData;
}

/** Performance Trajectory — the xG series plus per-match tooltip metadata. */
export interface TrajectoryPayload {
  series: TrajectoryData;
  matchInfo: TrajectoryMatchInfoData;
}

/** 1st/2nd Half Goals — the bars view + the per-band bands view. */
export interface HalfGoalsPayload {
  bars: HalfGoalsData;
  bands: HalfGoalsBandsData;
}

/**
 * BTTS / Clean Sheets — the donut view reads `rates`; the blocks view renders
 * recent results, so `form` (recent-form data) rides along.
 */
export interface MatchRatePayload {
  rates: RateData;
  form: FormData;
}

/** Match Goals/Cards Movement — series + line labels + per-bookmaker offsets. */
export interface GoalsCardsMovementPayload {
  series: GoalsCardsMovementData;
  labels: MovementLabels;
  bkOffsets: BkOffsets;
  /** Movement History back face (optional: see WinnerMovementPayload). */
  history?: LineHistoryData;
}

/** Winner (1X2) Movement — series + per-bookmaker offsets (no line labels),
 *  plus the Movement History back face's data (each team's last 10 matches).
 *  `history` is optional: a cache item seeded before it was added lacks it
 *  until the seeder is re-run, and the back face shows an empty state. */
export interface WinnerMovementPayload {
  series: WinnerMovementData;
  bkOffsets: BkOffsets;
  history?: MovementHistoryData;
}

/** Player Goals/Cards Movement — series + per-bookmaker offsets. */
export interface PlayerMovementPayload {
  series: PlayerMovementData;
  bkOffsets: BkOffsets;
  /** Movement History back face, keyed by player (optional: see WinnerMovementPayload). */
  history?: MovementHistoryData;
}

/** League / Form standings — full dataset keyed by the control (season, or ha-timeline). */
export type StandingsFullData = Record<string, readonly LeagueTeam[]>;
/** xG table — full dataset keyed by rate-timeline. */
export type XgTableFullData = Record<string, readonly XgTeam[]>;

// ─── The registry ───────────────────────────────────────────────────────────

/**
 * widget key → the response contract the endpoint returns for that widget.
 * Adding a widget = add one line here (+ its title below + its builder in
 * app-api). The `satisfies` on WIDGET_TITLES keeps the two exactly in sync.
 */
export interface WidgetPayloadMap {
  "correct-score": CorrectScoreGrid;
  "league-table": StandingsFullData;
  "form-table": StandingsFullData;
  "xg-table": XgTableFullData;
  "injuries-suspension": InjuryData;
  "doubtful-players": DoubtfulData;
  "news-sentiment": NewsData;
  "penalty-takers": PenaltyTakersData;
  "player-discipline": PlayerDisciplineData;
  "value-indicator": ValueIndicatorSources;
  "margin-calculator": MarginCalculatorData;
  "recent-form": FormData;
  "home-away-form": HomeAwayFormPayload;
  "top-scorers-league": TopScorersLeagueData;
  "top-scorers-club": TopScorersClubData;
  "assists-league": AssistsLeagueData;
  "assists-club": AssistsClubData;
  "shots-on-target-club": ShotsOnTargetData;
  "team-discipline": TeamDisciplineData;
  "scored-conceded": ScoredConcededData;
  "penalty-record": PenaltyRecordData;
  "over-under-rates": OverUnderData;
  "first-goal": FirstGoalData;
  "set-piece-goals": SetPieceData;
  btts: MatchRatePayload;
  "clean-sheets": MatchRatePayload;
  "goals-by-time-band": GtbData;
  "ht-ft": FormData;
  "referee-profile": RefereeProfileData;
  "referee-x-club": RefWithTeamsData;
  "referee-trend": RefTrendData;
  "match-states": MatchStatesData;
  "performance-trajectory": TrajectoryPayload;
  "xg-timeline": XgTimelineData;
  "half-goals": HalfGoalsPayload;
  "head-to-head": H2HData;
  "head-to-head-trends": H2HData;
  "winner-1x2": WinnerData;
  "winner-1x2-movement": WinnerMovementPayload;
  "match-goals": GoalsCardsData;
  "match-cards": GoalsCardsData;
  "match-goals-movement": GoalsCardsMovementPayload;
  "match-cards-movement": GoalsCardsMovementPayload;
  "player-goals": PlayerMarketData;
  "player-goals-movement": PlayerMovementPayload;
  "player-cards": PlayerMarketData;
  "player-cards-movement": PlayerMovementPayload;
}

export type WidgetKey = keyof WidgetPayloadMap;

/**
 * widget key → its module title (as it appears in the menu / REGISTRY). The
 * `satisfies` clause forces this map to have EXACTLY the WidgetPayloadMap keys,
 * so a widget can never be added to one and forgotten in the other.
 */
export const WIDGET_TITLES = {
  "correct-score": "Correct Score Matrix",
  "league-table": "League Table",
  "form-table": "Form Table",
  "xg-table": "xG Table",
  "injuries-suspension": "Injuries & Suspension",
  "doubtful-players": "Doubtful Players",
  "news-sentiment": "News & Sentiment",
  "penalty-takers": "Penalty Takers",
  "player-discipline": "Player Discipline",
  "value-indicator": "Value Indicator",
  "margin-calculator": "Margin Calculator",
  "recent-form": "Recent Form",
  "home-away-form": "Home/Away Form",
  "top-scorers-league": "Top Scorers — League",
  "top-scorers-club": "Top Scorers — Club",
  "assists-league": "Assists — League",
  "assists-club": "Assists — Club",
  "shots-on-target-club": "Shots on Target — Club",
  "team-discipline": "Team Discipline",
  "scored-conceded": "Scored & Conceded",
  "penalty-record": "Penalty Record",
  "over-under-rates": "Over / Under Rates",
  "first-goal": "First Goal",
  "set-piece-goals": "Set Piece Goals",
  btts: "BTTS",
  "clean-sheets": "Clean Sheets",
  "goals-by-time-band": "Goals by Time Band",
  "ht-ft": "HT/FT",
  "referee-profile": "Referee Profile",
  "referee-x-club": "Referee x Club",
  "referee-trend": "Referee Trend",
  "match-states": "Match States",
  "performance-trajectory": "Performance Trajectory",
  "xg-timeline": "xG Timeline",
  "half-goals": "1st/2nd Half Goals",
  "head-to-head": "Head to Head",
  "head-to-head-trends": "Head to Head Trends",
  "winner-1x2": "Winner (1x2)",
  "winner-1x2-movement": "Winner (1x2) - Movement",
  "match-goals": "Match Goals",
  "match-cards": "Match Cards",
  "match-goals-movement": "Match Goals - Movement",
  "match-cards-movement": "Match Cards - Movement",
  "player-goals": "Player Goals",
  "player-goals-movement": "Player Goals - Movement",
  "player-cards": "Player Cards",
  "player-cards-movement": "Player Cards - Movement",
} as const satisfies Record<WidgetKey, string>;

/** The allowlist the reader validates against + the seeder iterates. */
export const WIDGET_KEYS = Object.keys(WIDGET_TITLES) as WidgetKey[];

/** Type guard: is `x` a known widget key? (the reader's 404 gate). */
export function isWidgetKey(x: string): x is WidgetKey {
  return Object.prototype.hasOwnProperty.call(WIDGET_TITLES, x);
}
